import { useEffect, useRef, useState } from "react";
import { Trash2, ExternalLink, Eye } from "lucide-react";
import { streamUrl, BRAND_VIDEO_POSTER, trackVideoView } from "@/lib/api";
import ShareButtons from "@/components/ShareButtons";

export function ViewsBadge({ count, className = "" }) {
  return (
    <span
      data-testid="video-views-badge"
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase ${className}`}
    >
      <Eye size={12} /> {Intl.NumberFormat("en", { notation: "compact" }).format(count || 0)}
    </span>
  );
}

export default function VideoCard({ video, canDelete = false, onDelete, index = 0 }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const entityPath = video.entity_type === "restaurant" ? `/directory/${video.entity_id}` : `/profile/${video.entity_id}`;
  const shareLink = `${origin}${entityPath}`;
  const cardRef = useRef(null);
  const counted = useRef(false);
  const [views, setViews] = useState(video.views || 0);

  useEffect(() => {
    const node = cardRef.current;
    if (!node || counted.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !counted.current) {
          counted.current = true;
          trackVideoView(video.id).then((n) => { if (typeof n === "number") setViews(n); });
          obs.disconnect();
        }
      });
    }, { threshold: 0.5 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [video.id]);

  return (
    <div
      ref={cardRef}
      data-testid={`video-card-${video.id}`}
      className="bg-white border border-sand-200 animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 500)}ms` }}
    >
      <div className="aspect-video bg-sand-900 relative">
        <div className="absolute top-2 right-2 z-10 bg-sand-900/80 text-sand-50 px-2 py-1 backdrop-blur-sm pointer-events-none">
          <ViewsBadge count={views} />
        </div>
        {video.video_type === "upload" ? (
          <video src={streamUrl(video.id)} poster={video.thumbnail_url || BRAND_VIDEO_POSTER} controls className="w-full h-full object-contain bg-black" preload="metadata" />
        ) : video.provider === "youtube" || video.provider === "vimeo" || video.provider === "tiktok" || video.provider === "instagram" ? (
          <iframe
            src={video.embed_url}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <a href={video.source_url} target="_blank" rel="noreferrer" className="w-full h-full flex items-center justify-center text-sand-50 gap-2">
            <ExternalLink size={18} /> Watch externally
          </a>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-lg font-bold text-sand-900 leading-tight">{video.title}</h3>
          {canDelete && (
            <button
              onClick={() => onDelete?.(video.id)}
              data-testid={`delete-video-${video.id}`}
              className="text-sand-400 hover:text-brand-500 transition-colors shrink-0"
              aria-label="Delete video"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
        {video.entity_name && (
          <div className="mt-1 font-mono text-[10px] tracking-widest uppercase text-forest-500">{video.entity_name}</div>
        )}
        {video.description && <p className="mt-2 text-sm text-sand-700 font-light line-clamp-2">{video.description}</p>}
        <div className="mt-4 pt-3 border-t border-sand-100">
          <ShareButtons url={shareLink} title={video.title} />
        </div>
      </div>
    </div>
  );
}
